import React, { useState, useEffect, useCallback } from "react";
import { Review } from "../../types/ReviewTypes";
import ReviewItem from "./ReviewItem";
import ReviewForm from "./ReviewForm";
import { authFetch, isLoggedIn } from "../../utils/authUtils";
import "./Review.css";

interface AlbumReviewsContainerProps {
    albumId: number;
}

const AlbumReviewsContainer: React.FC<AlbumReviewsContainerProps> = ({ albumId }) => {
    const [reviews, setReviews] = useState<Review[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedReview, setSelectedReview] = useState<Review | undefined>(undefined);
    const [userHasReview, setUserHasReview] = useState(false);

    const fetchReviews = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await authFetch(`/api/reviews/album/${albumId}`);

            if (!response.ok) {
                throw new Error(`Error: ${response.status}`);
            }

            const data = await response.json();
            setReviews(data);

            // Check if the current user has already reviewed this album
            setUserHasReview(data.some((review: Review) => review.is_owner));
        } catch (err) {
            console.error("Error fetching album reviews:", err);
            setError("Failed to load reviews");
        } finally {
            setIsLoading(false);
        }
    }, [albumId]);

    useEffect(() => {
        fetchReviews();
    }, [albumId, fetchReviews]);

    useEffect(() => {
        const handleReviewsListUpdate = (event: Event) => {
            // Check if this event is for our album
            const customEvent = event as CustomEvent;
            if (customEvent.detail?.itemId === albumId && customEvent.detail?.itemType === 'album') {
                console.log("AlbumReviewsContainer received update event, refreshing reviews");
                fetchReviews();
            }
        };

        // Add event listener
        window.addEventListener('reviewsListUpdated', handleReviewsListUpdate);
        window.addEventListener('reviewUpdated', fetchReviews);

        // Clean up
        return () => {
            window.removeEventListener('reviewsListUpdated', handleReviewsListUpdate);
            window.removeEventListener('reviewUpdated', fetchReviews);
        };
    }, [albumId, fetchReviews]);

    const handleAddReview = () => {
        setSelectedReview(undefined);
        setIsModalOpen(true);
    };

    const handleEditReview = (review: Review) => {
        setSelectedReview(review);
        setIsModalOpen(true);
    };

    const handleReviewAdded = (newReview: Review) => {
        setReviews(prevReviews => [newReview, ...prevReviews]);
        setUserHasReview(true);
    };

    const handleReviewUpdated = (updatedReview: Review) => {
        setReviews(prevReviews =>
            prevReviews.map(review =>
                review.id === updatedReview.id ? updatedReview : review
            )
        );
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    return (
        <div className="reviews-container">
            <div className="reviews-header">
                <h3>reviews</h3>

                {isLoggedIn() && !userHasReview && (
                    <button
                        className="add-review-button"
                        onClick={handleAddReview}
                    >
                        <i className="fa fa-plus" aria-hidden="true" />
                    </button>
                )}

                {isLoggedIn() && userHasReview && (
                    <div className="review-status">You've already reviewed this album</div>
                )}

                {!isLoggedIn() && (
                    <div className="login-prompt">
                        <a href="/login">Log in</a> to write a review
                    </div>
                )}
            </div>

            {isLoading ? (
                <div className="loading-reviews">Loading reviews...</div>
            ) : error ? (
                <div className="error-message">{error}</div>
            ) : reviews.length === 0 ? (
                <div className="no-reviews">
                    No reviews yet. Be the first to review this album!
                </div>
            ) : (
                <div className="reviews-list">
                    {reviews.map(review => (
                        <ReviewItem
                            key={review.id}
                            review={review}
                            onEditReview={handleEditReview}
                        />
                    ))}
                </div>
            )}

            <ReviewForm
                itemId={albumId}
                itemType="album"
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                existingReview={selectedReview}
                onReviewAdded={handleReviewAdded}
                onReviewUpdated={handleReviewUpdated}
            />
        </div>
    );
};

export default AlbumReviewsContainer; 